import { canonicalEntityIdForNode, type GraphNode, type GraphVisualizationFrame } from "@salesko/contracts";

export type CanonicalIdentityDraft = {
  mergeTargetNodeId: string;
  rekeyLabel: string;
  splitLabel: string;
};

export type CanonicalMergeTargetSuggestion = {
  node: GraphNode;
  reasons: string[];
  score: number;
};

export type CanonicalSplitNodeRef = {
  frameRecordId: string;
  nodeId: string;
};

export function canonicalEntityIdForGraphNode(node: Pick<GraphNode, "label" | "type">): string {
  return canonicalEntityIdForNode(node.type, node.label);
}

export function canonicalIdentityDraftFromNode(node: GraphNode | null): CanonicalIdentityDraft {
  return {
    mergeTargetNodeId: "",
    rekeyLabel: node?.label ?? "",
    splitLabel: node?.label ? `${node.label} copy` : ""
  };
}

export function sameTypeCanonicalMergeTargets(
  frame: GraphVisualizationFrame | null,
  sourceNode: GraphNode | null
): GraphNode[] {
  return canonicalMergeTargetSuggestions(frame, sourceNode).map((suggestion) => suggestion.node);
}

export function canonicalMergeTargetSuggestions(
  frame: GraphVisualizationFrame | null,
  sourceNode: GraphNode | null
): CanonicalMergeTargetSuggestion[] {
  if (!frame || !sourceNode) {
    return [];
  }

  const sourceCanonicalEntityId = canonicalEntityIdForGraphNode(sourceNode);
  const seenCanonicalEntityIds = new Set<string>();
  const suggestions: CanonicalMergeTargetSuggestion[] = [];

  for (const candidate of frame.graph.nodes) {
    if (candidate.id === sourceNode.id || candidate.type !== sourceNode.type) {
      continue;
    }

    const candidateCanonicalEntityId = canonicalEntityIdForGraphNode(candidate);
    if (candidateCanonicalEntityId === sourceCanonicalEntityId || seenCanonicalEntityIds.has(candidateCanonicalEntityId)) {
      continue;
    }

    seenCanonicalEntityIds.add(candidateCanonicalEntityId);
    const reasons = canonicalMergeReasons(frame, sourceNode, candidate);
    suggestions.push({
      node: candidate,
      reasons,
      score: reasons.reduce((total, reason) => total + mergeReasonScore(reason), 0)
    });
  }

  return suggestions.sort((left, right) => {
    if (right.score !== left.score) {
      return right.score - left.score;
    }
    return left.node.label.localeCompare(right.node.label);
  });
}

export function splitNodeRef(frameRecordId: string, node: GraphNode): CanonicalSplitNodeRef {
  return {
    frameRecordId,
    nodeId: node.id
  };
}

function canonicalMergeReasons(frame: GraphVisualizationFrame, sourceNode: GraphNode, candidate: GraphNode) {
  return uniqueStrings([
    labelsLookSimilar(sourceNode.label, candidate.label) ? "similar label" : undefined,
    sharedScalarValue(sourceNode, candidate, ["email", "emailAddress", "email_address"]) ? "same email" : undefined,
    sharedScalarValue(sourceNode, candidate, ["domain", "website", "site"]) ? "same domain" : undefined,
    sharedScalarValue(sourceNode, candidate, ["company", "organisation", "organization", "account"])
      ? "same company"
      : undefined,
    previousLabelMatches(sourceNode, candidate) || previousLabelMatches(candidate, sourceNode)
      ? "previous label match"
      : undefined,
    canonicalProvenanceMatches(sourceNode, candidate) || canonicalProvenanceMatches(candidate, sourceNode)
      ? "canonical provenance"
      : undefined,
    sharedNeighborNodeIds(frame, sourceNode.id, candidate.id).length > 0 ? "shared neighbor" : undefined
  ]);
}

function labelsLookSimilar(left: string, right: string) {
  const leftCompact = compactLabel(left);
  const rightCompact = compactLabel(right);
  if (!leftCompact || !rightCompact) {
    return false;
  }
  if (leftCompact === rightCompact) {
    return true;
  }
  if (Math.min(leftCompact.length, rightCompact.length) >= 6) {
    return leftCompact.includes(rightCompact) || rightCompact.includes(leftCompact);
  }

  const leftTokens = labelTokens(left);
  const rightTokens = new Set(labelTokens(right));
  const overlap = leftTokens.filter((token) => rightTokens.has(token)).length;
  return overlap >= 2 || (overlap >= 1 && Math.max(leftTokens.length, rightTokens.size) <= 2);
}

function compactLabel(label: string) {
  return label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function labelTokens(label: string) {
  return label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 1);
}

function sharedScalarValue(left: GraphNode, right: GraphNode, keys: string[]) {
  const leftValues = valuesForKeys(left.data, keys);
  const rightValues = new Set(valuesForKeys(right.data, keys));
  return leftValues.some((value) => rightValues.has(value));
}

function valuesForKeys(data: Record<string, unknown>, keys: string[]) {
  return uniqueStrings(keys.flatMap((key) => stringValues(data[key]).map(normalizeScalarValue)));
}

function normalizeScalarValue(value: string) {
  return value.trim().toLowerCase();
}

function previousLabelMatches(node: GraphNode, other: GraphNode) {
  const otherLabel = compactLabel(other.label);
  return stringValues(node.data.previousLabels)
    .map(compactLabel)
    .some((label) => label.length > 0 && label === otherLabel);
}

function canonicalProvenanceMatches(node: GraphNode, other: GraphNode) {
  const otherCanonicalEntityId = canonicalEntityIdForGraphNode(other);
  return [
    ...stringValues(node.data.previousCanonicalEntityIds),
    ...stringValues(node.data.mergedFromCanonicalEntityIds),
    ...stringValues(node.data.splitFromCanonicalEntityId)
  ].includes(otherCanonicalEntityId);
}

function sharedNeighborNodeIds(frame: GraphVisualizationFrame, leftNodeId: string, rightNodeId: string) {
  const leftNeighbors = new Set(neighborNodeIds(frame, leftNodeId));
  return neighborNodeIds(frame, rightNodeId).filter((nodeId) => leftNeighbors.has(nodeId));
}

function neighborNodeIds(frame: GraphVisualizationFrame, nodeId: string) {
  return frame.graph.edges.flatMap((edge) => {
    if (edge.source === nodeId) {
      return [edge.target];
    }
    if (edge.target === nodeId) {
      return [edge.source];
    }
    return [];
  });
}

function stringValues(value: unknown): string[] {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap(stringValues);
  }
  return [];
}

function uniqueStrings(values: Array<string | undefined>) {
  return [...new Set(values.filter((value): value is string => Boolean(value?.trim())).map((value) => value.trim()))];
}

function mergeReasonScore(reason: string) {
  switch (reason) {
    case "same email":
      return 100;
    case "same domain":
      return 90;
    case "canonical provenance":
      return 85;
    case "previous label match":
      return 80;
    case "same company":
      return 60;
    case "similar label":
      return 45;
    case "shared neighbor":
      return 25;
    default:
      return 0;
  }
}
